export const PALETTE = [
  { type: "text",     label: "Input",        icon: "text" },
  { type: "textarea", label: "Text Area",     icon: "textarea" },
  { type: "email",    label: "Email",         icon: "email" },
  { type: "number",   label: "Number",        icon: "123" },
  { type: "amount",   label: "Amount",        icon: "currency" },
  { type: "select",   label: "Dropdown",      icon: "select",  options: ["Option 1", "Option 2"] },
  { type: "radio",    label: "Radio group",   icon: "radio",   options: ["Option A", "Option B"] },
  { type: "checkbox", label: "Checkbox",      icon: "checkbox" ,   options: ["Option A", "Option B"]},
  { type: "date",     label: "Date",          icon: "calendar" },
  { type: "phone",    label: "Phone Number",  icon: "number" },
  { type: "address",  label: "Address",       icon: "address" },
];

export const MAX_FIELDS = 50;

// Amount, Email, and Phone are the true mandatory/locked fields. They are
// always injected into every form, can never be deleted, and are excluded
// from id-generation so extra instances dropped from the palette get clean
// sequential ids (email, email2, email3... / phone, phone2, phone3...)
// instead of colliding with the locked mandatory-* ids.
export const MANDATORY_FIELD_IDS = [
  "mandatory-amount",
  // "mandatory-email",
  // "mandatory-phone",
];

/**
 * Generates a unique field ID for a newly dropped field.
 *
 * @param {string} type - Field type (e.g. "text", "email", "address")
 * @param {Array}  fields - Current fields array
 * @param {string[]} [excludeIds] - IDs to treat as non-user fields (e.g. mandatory IDs)
 */
export function generateFieldId(type, fields, excludeIds = MANDATORY_FIELD_IDS) {
  const existing = fields.filter(
    (f) => f.type === type && !excludeIds.includes(f.id),
  );
  if (!existing.length) return type;
  let max = 1;
  existing.forEach((f) => {
    if (f.id === type) { max = Math.max(max, 1); return; }
    const m = f.id.match(new RegExp(`^${type}(\\d+)$`));
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  });
  return `${type}${max + 1}`;
}

/**
 * Generates a unique label for a newly dropped field, e.g.
 * "Phone Number", "Phone Number2", "Phone Number3"...
 *
 * @param {string} baseLabel - The palette item's default label
 * @param {Array}  fields    - Current fields array
 */
export function generateFieldLabel(baseLabel, fields) {
  const used = new Set(fields.map((f) => (f.label || "").trim().toLowerCase()));
  const normalizedBase = baseLabel.trim().toLowerCase();
  if (!used.has(normalizedBase)) return baseLabel;
  let n = 2;
  while (used.has(`${baseLabel}${n}`.trim().toLowerCase())) n++;
  return `${baseLabel}${n}`;
}

/**
 * Builds a new field instance from a palette entry. Auto-generates a unique
 * label so repeat drags of the same field type never collide (Amount,
 * Amount2, Amount3...), which also sidesteps the duplicate-label guard.
 */
export function buildFieldInstance(paletteItem, id, fields = []) {
  return {
    id,
    type:               paletteItem.type,
    label:              generateFieldLabel(paletteItem.label, fields),
    options:            paletteItem.options || [],
    required:           false,
    mandatory:          false,
    readOnly:           false,
    splitRequired:      false,
    fixedValueEnabled:  false,
    fixedValue:         "",
    receiptEnabled: false,
    receiptEmailEnabled:false
  };
}

/**
 * Generic drag-end handler for palette → canvas and canvas ↔ canvas moves.
 *
 * @param {object}   result            - @hello-pangea/dnd result object
 * @param {Array}    fields            - current fields array
 * @param {Function} setFields         - fields setter
 * @param {object}   [opts]
 * @param {boolean}  [opts.lockSearchKey] - prevent moving the search-key field (FormSetup)
 * @param {Function} [opts.onMaxFields]   - called when MAX_FIELDS is exceeded
 */
export function handlePaletteToCanvasDrag(result, fields, setFields, opts = {}) {
  const { lockSearchKey = false, onMaxFields } = opts;

  if (!result.destination) return;

  const { source, destination } = result;

  // ── Palette → Canvas ──────────────────────────────────────────────────────
  if (source.droppableId === "palette" && destination.droppableId === "canvas") {
    if (fields.length >= MAX_FIELDS) {
      onMaxFields?.();
      return;
    }
    const paletteItem = PALETTE[source.index];
    const id          = generateFieldId(paletteItem.type, fields);
    const instance    = buildFieldInstance(paletteItem, id, fields);

    const arr  = Array.from(fields);
    const dest = lockSearchKey && fields[0]?.isSearchKey && destination.index === 0
      ? 1
      : destination.index;
    arr.splice(dest, 0, instance);
    setFields(arr);
    return;
  }

  // ── Canvas → Canvas (reorder) ─────────────────────────────────────────────
  if (source.droppableId === "canvas" && destination.droppableId === "canvas") {
    if (lockSearchKey) {
      if (source.index === 0 && fields[0]?.isSearchKey) return;
      if (destination.index === 0 && fields[0]?.isSearchKey) return;
    }
    const arr = Array.from(fields);
    const [moved] = arr.splice(source.index, 1);
    arr.splice(destination.index, 0, moved);
    setFields(arr);
  }
}

/**
 * Strips fixedValue when fixedValueEnabled is false and normalises boolean
 * field flags — applied before sending to the API. "address" fields are
 * treated like any free-text field here (no special-casing needed since the
 * backend just stores fixedValue/options/etc. generically per type).
 *
 * Amount is always included in the receipt: if this is the mandatory Amount
 * field, receiptEnabled is forced to true here as a final safeguard,
 * regardless of what the UI state happened to be.
 */
export function normalizeFieldsForApi(fields) {
  return fields.map((f) => ({
    ...f,
    readOnly:          f.readOnly          === true,
    splitRequired:     f.splitRequired     === true,
    fixedValueEnabled: f.fixedValueEnabled === true,
    receiptEmailEnabled: f.receiptEmailEnabled === true,
    receiptEnabled:    (f.type === "amount" && f.mandatory) ? true : f.receiptEnabled === true,
    fixedValue:        f.fixedValueEnabled === true ? (f.fixedValue ?? "") : "",
    cascadeEnabled: f.cascadeEnabled ?? false,
parentFieldId: f.parentFieldId ?? null,
cascadeLevel: f.cascadeLevel ?? 1,
cascadeMappings: f.cascadeMappings ?? {},
  }));
}